import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { getIcon, getColor } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { timeAgo } from '@/lib/time';
import { UserPlus, Pencil, Trash2, ShieldCheck, Eye, Loader2, Crown, UserCheck, Clock, Check, X, LayoutGrid } from 'lucide-react';

function AppAccessPicker({ apps, mode, setMode, appIds, setAppIds }) {
  const toggle = (id) => setAppIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  return (
    <div className="space-y-2">
      <Label className="text-[color:var(--cx-fg-muted)]">App access</Label>
      <div className="grid grid-cols-2 gap-2">
        {[['all', 'All apps'], ['custom', 'Specific apps']].map(([val, label]) => (
          <button type="button" key={val} onClick={() => setMode(val)} data-testid={`team-access-mode-${val}`}
            className={`rounded-lg border px-3 py-2 text-sm transition-colors ${mode === val ? 'border-[color:var(--cx-primary)] bg-[rgba(46,230,197,0.12)] text-[color:var(--cx-fg)]' : 'border-[color:var(--cx-border)] text-[color:var(--cx-fg-muted)] hover:border-[rgba(46,230,197,0.4)]'}`}>
            {label}
          </button>
        ))}
      </div>
      {mode === 'custom' && (
        <div className="max-h-48 overflow-auto rounded-lg border border-[color:var(--cx-border)] p-2 space-y-1" data-testid="team-access-app-list">
          {apps.length === 0 && <p className="text-xs text-[color:var(--cx-muted)] p-2">No apps yet.</p>}
          {apps.map((a) => {
            const Icon = getIcon(a.icon);
            const color = getColor(a.color);
            const on = appIds.includes(a.id);
            return (
              <button type="button" key={a.id} onClick={() => toggle(a.id)} data-testid="team-access-app-option" data-slug={a.slug} data-selected={on}
                className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${on ? 'bg-[rgba(46,230,197,0.10)]' : 'hover:bg-[color:var(--cx-surface-solid)]'}`}>
                <span className="h-7 w-7 rounded-md flex items-center justify-center border border-[color:var(--cx-border)]" style={{ background: `linear-gradient(135deg, ${color.from}22, ${color.to}14)` }}>
                  <Icon className="h-3.5 w-3.5" style={{ color: color.from }} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-[color:var(--cx-fg)] truncate">{a.name}</span>
                  <span className="block font-mono text-[10px] text-[color:var(--cx-muted)]">/{a.slug}</span>
                </span>
                <span className={`h-5 w-5 rounded-full border flex items-center justify-center ${on ? 'bg-[color:var(--cx-primary)] border-[color:var(--cx-primary)]' : 'border-[color:var(--cx-border)]'}`}>
                  {on && <Check className="h-3 w-3 text-[#04120f]" />}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <p className="text-xs text-[color:var(--cx-muted)]">{mode === 'all' ? 'This person can see every app.' : 'This person will only see the apps you tick.'}</p>
    </div>
  );
}

function TeamDialog({ open, onOpenChange, initial, onSave, saving, apps }) {
  const isEdit = Boolean(initial);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [permission, setPermission] = useState('view');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('all');
  const [appIds, setAppIds] = useState([]);
  const [err, setErr] = useState('');

  React.useEffect(() => {
    if (open) {
      setEmail(initial?.email || '');
      setName(initial?.name || '');
      setPermission(initial?.permission || 'view');
      setPassword('');
      setMode(initial?.app_access_mode || 'all');
      setAppIds(initial?.app_ids || []);
      setErr('');
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!isEdit && !email.includes('@')) { setErr('Enter a valid email.'); return; }
    if (!isEdit && password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (isEdit && password && password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    const access = permission === 'view'
      ? { app_access_mode: mode, app_ids: mode === 'custom' ? appIds : [] }
      : { app_access_mode: 'all', app_ids: [] };
    const payload = isEdit
      ? { name, permission, ...access, ...(password ? { password } : {}) }
      : { email, name, permission, password, ...access };
    try {
      await onSave(payload);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not save member.');
    }
  };

  const lockPermission = initial?.is_owner;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[color:var(--cx-surface-solid)] border-[color:var(--cx-border)] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? 'Edit member' : 'Add team member'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update this person’s access.' : 'Create a login for someone on your team (pre-approved).'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[color:var(--cx-fg-muted)]">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isEdit} placeholder="teammate@cxatools.com" data-testid="team-member-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[color:var(--cx-fg-muted)]">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="team-member-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[color:var(--cx-fg-muted)]">Permission</Label>
            <Select value={permission} onValueChange={setPermission} disabled={lockPermission}>
              <SelectTrigger data-testid="team-member-permission"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View only — can open apps</SelectItem>
                <SelectItem value="manage">Manage — full admin access</SelectItem>
              </SelectContent>
            </Select>
            {lockPermission && <p className="text-xs text-[color:var(--cx-muted)]">The owner always keeps manage access.</p>}
          </div>

          {permission === 'view' ? (
            <AppAccessPicker apps={apps} mode={mode} setMode={setMode} appIds={appIds} setAppIds={setAppIds} />
          ) : (
            <div className="rounded-lg border border-[color:var(--cx-border)] p-3 text-xs text-[color:var(--cx-muted)] flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--cx-primary)]" />Managers can access every app.
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-[color:var(--cx-fg-muted)]">{isEdit ? 'Reset password (optional)' : 'Password'}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isEdit ? 'Leave blank to keep current' : 'At least 8 characters'} autoComplete="new-password" data-testid="team-member-password" />
          </div>
          {err && <p className="text-sm text-[color:var(--destructive)]" data-testid="team-dialog-error">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="team-save-member-button">
              {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>) : (isEdit ? 'Save' : 'Add member')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PermissionBadge({ permission }) {
  const manage = permission === 'manage';
  return (
    <Badge className="shrink-0" style={{ background: manage ? 'rgba(46,230,197,0.14)' : 'var(--cx-surface-solid)', color: manage ? 'var(--cx-primary)' : 'var(--cx-fg-muted)', border: '1px solid var(--cx-border)' }}>
      {manage ? <ShieldCheck className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
      {manage ? 'Manage' : 'View only'}
    </Badge>
  );
}

export function TeamPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [approvePerm, setApprovePerm] = useState({});

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: async () => (await api.get('/team')).data,
  });
  const { data: apps = [] } = useQuery({
    queryKey: ['apps', 'all'],
    queryFn: async () => (await api.get('/apps')).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['team'] });

  const createMut = useMutation({
    mutationFn: (payload) => api.post('/team', payload),
    onSuccess: () => { invalidate(); setDialogOpen(false); toast.success('Member added'); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => api.put(`/team/${id}`, payload),
    onSuccess: () => { invalidate(); setDialogOpen(false); toast.success('Member updated'); },
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/team/${id}`),
    onSuccess: () => { invalidate(); setDeleteTarget(null); toast.success('Removed'); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not remove member'),
  });
  const approveMut = useMutation({
    mutationFn: ({ id, permission }) => api.post(`/team/${id}/approve`, { permission }),
    onSuccess: () => { invalidate(); toast.success('Access approved'); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not approve'),
  });

  const handleSave = async (payload) => {
    if (editing) await updateMut.mutateAsync({ id: editing.id, payload });
    else await createMut.mutateAsync(payload);
  };

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (m) => { setEditing(m); setDialogOpen(true); };

  const accessLabel = (m) => {
    if (m.permission === 'manage') return 'All apps';
    if (m.app_access_mode === 'custom') {
      const n = (m.app_ids || []).length;
      return `${n} ${n === 1 ? 'app' : 'apps'}`;
    }
    return 'All apps';
  };

  const pending = members.filter((m) => m.status === 'pending');
  const active = members.filter((m) => m.status !== 'pending');

  return (
    <div className="space-y-5">
      {(isLoading || pending.length > 0) && (
        <div className="cx-glass rounded-2xl overflow-hidden border-[rgba(46,230,197,0.35)]" data-testid="team-pending-section">
          <div className="p-4 border-b border-[color:var(--cx-border)] flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-[color:var(--cx-primary)]" />
            <h2 className="font-display text-[color:var(--cx-fg)]">Pending approvals</h2>
            {pending.length > 0 && <Badge className="ml-1" style={{ background: 'rgba(46,230,197,0.14)', color: 'var(--cx-primary)', border: '1px solid var(--cx-border)' }}>{pending.length}</Badge>}
          </div>
          <div className="p-3 space-y-2.5">
            {isLoading ? (
              <Skeleton className="h-16 rounded-xl bg-[color:var(--cx-surface-solid)]" />
            ) : (
              pending.map((m) => {
                const perm = approvePerm[m.id] || 'view';
                return (
                  <div key={m.id} className="rounded-xl border border-[color:var(--cx-border)] bg-[color:var(--cx-surface-solid)] p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="team-pending-row" data-email={m.email}>
                    <span className="h-10 w-10 rounded-full bg-[color:var(--cx-bg-elev)] border border-[color:var(--cx-border)] flex items-center justify-center text-sm font-medium text-[color:var(--cx-primary)]">
                      {(m.name || m.email).slice(0, 2).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2"><span className="font-display text-[color:var(--cx-fg)]">{m.name || m.email.split('@')[0]}</span><Badge variant="outline" className="text-[10px]"><Clock className="h-3 w-3 mr-1" />Pending</Badge></div>
                      <div className="truncate text-xs text-[color:var(--cx-muted)]">{m.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={perm} onValueChange={(v) => setApprovePerm((s) => ({ ...s, [m.id]: v }))}>
                        <SelectTrigger className="w-[130px]" data-testid="team-approve-permission"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">View only</SelectItem>
                          <SelectItem value="manage">Manage</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => approveMut.mutate({ id: m.id, permission: perm })} data-testid="team-approve-button"><Check className="h-4 w-4 mr-1.5" />Approve</Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(m)} data-testid="team-reject-button" className="text-[color:var(--destructive)]"><X className="h-4 w-4 mr-1.5" />Reject</Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg text-[color:var(--cx-fg)]">Team access</h2>
          <p className="text-sm text-[color:var(--cx-fg-muted)]">Choose who can manage vs view, and which apps each person sees.</p>
        </div>
        <Button onClick={openAdd} data-testid="team-add-button"><UserPlus className="h-4 w-4 mr-1.5" />Add member</Button>
      </div>

      {isLoading ? (
        <div className="space-y-2.5">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-2xl bg-[color:var(--cx-surface-solid)]" />)}</div>
      ) : (
        <div className="space-y-2.5" data-testid="team-list">
          {active.map((m) => {
            const isSelf = m.id === user?.id;
            const canDelete = !m.is_owner && !isSelf;
            return (
              <div key={m.id} className="cx-glass rounded-2xl p-4 flex items-center gap-3" data-testid="team-member-row" data-email={m.email}>
                <span className="h-10 w-10 rounded-full bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center text-sm font-medium text-[color:var(--cx-primary)]">
                  {(m.name || m.email).slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-[color:var(--cx-fg)]">{m.name || m.email.split('@')[0]}</span>
                    {m.is_owner && <Badge variant="outline" className="text-[10px]"><Crown className="h-3 w-3 mr-1" />Owner</Badge>}
                    {isSelf && <Badge variant="outline" className="text-[10px]">You</Badge>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[color:var(--cx-muted)]">
                    <span className="truncate">{m.email}</span>
                    <span className="inline-flex items-center gap-1"><LayoutGrid className="h-3 w-3" />{accessLabel(m)}</span>
                  </div>
                  <div className="mt-0.5 text-xs" data-testid="team-last-access" data-email={m.email}>
                    {m.last_access ? (
                      <span className="inline-flex items-center gap-1 text-[color:var(--cx-fg-muted)]">
                        <Clock className="h-3 w-3 text-[color:var(--cx-primary)]" />Opened {m.last_access.app_name || m.last_access.slug} · {timeAgo(m.last_access.at)}
                      </span>
                    ) : (
                      <span className="text-[color:var(--cx-muted)] opacity-70">No activity yet</span>
                    )}
                  </div>
                </div>
                <PermissionBadge permission={m.permission} />
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid="team-edit-member-button" title="Edit"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(m)} disabled={!canDelete} data-testid="team-delete-member-button" title={canDelete ? 'Remove' : 'Cannot remove'} className="text-[color:var(--destructive)] disabled:opacity-30"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TeamDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSave={handleSave} saving={createMut.isPending || updateMut.isPending} apps={apps} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[color:var(--cx-surface-solid)] border-[color:var(--cx-border)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{deleteTarget?.status === 'pending' ? 'Reject' : 'Remove'} {deleteTarget?.name || deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.status === 'pending' ? 'This declines their access request and deletes it.' : 'They will lose access to the portal immediately.'} This can’t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMut.mutate(deleteTarget.id)} className="bg-[color:var(--destructive)] text-white hover:opacity-90" data-testid="team-confirm-delete-button">{deleteTarget?.status === 'pending' ? 'Reject' : 'Remove'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
